<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { InfoIcon } from '@uiKit/icons'
import Text from '@uiKit/elements/Text.vue'
import TransactionFactCard, { type TransactionFact } from './TransactionFactCard.vue'
import { computed } from 'vue'

interface Props {
  /** Pre-formatted deposit amount, e.g. `"5,000"`. */
  amount: string
  currency?: string
  /** The bank reference the investor supplied on the confirm step. */
  bankReference: string
  /** Pre-formatted submission timestamp, e.g. `"24 Mar 2026 · 16:04"`. */
  submittedAt: string
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'AED',
})

const NEXT_STEPS = [
  'Transfer details submitted with receipt.',
  'Sliceal verifies your transfer against the submitted details.',
  'Funds credited to your wallet and you receive a notification.',
]

const summaryFacts = computed<TransactionFact[]>(() => [
  { label: 'Amount', value: `${props.currency} ${props.amount}` },
  { label: 'Bank Reference', value: props.bankReference || '-' },
  { label: 'Submitted', value: props.submittedAt },
  { label: 'Status', value: 'Pending' },
])
</script>

<template>
  <Box direction="col" gap="6" class="dss">
    <Box direction="col" align="center" gap="3" class="dss__hero">
      <Box flex align="center" justify="center" class="dss__tick">
        <PiIcon name="check" class="dss__tick-icon" />
      </Box>
      <Box direction="col" align="center" gap="1">
        <Text type="title-lg" align="center" is="h3">Submission Received</Text>
        <Text type="body" color="muted" align="center" is="p">
          Your deposit will be reflected in your wallet within 1–2 working days.
        </Text>
      </Box>
    </Box>

    <Box gap="6" align="start" class="dss__cards">
      <TransactionFactCard
        title="Deposit Summary"
        :facts="summaryFacts"
        layout="stacked"
        class="dss__card"
      />

      <Box direction="col" gap="4" is="section" class="dss__next">
        <Text type="body" weight="bold" color="muted" transform="uppercase" is="h4">
          What happens next
        </Text>
        <Box direction="col" gap="3" is="ol" class="dss__steps">
          <Box
            v-for="(step, index) in NEXT_STEPS"
            :key="step"
            is="li"
            align="start"
            gap="3"
            class="dss__step"
          >
            <Box flex align="center" justify="center" class="dss__step-num">
              <Text type="caption" weight="semibold" color="default" is="span">
                {{ index + 1 }}
              </Text>
            </Box>
            <Text type="body" color="muted" is="span" class="dss__step-text">{{ step }}</Text>
          </Box>
        </Box>
      </Box>
    </Box>

    <Box align="start" gap="3" class="dss__note">
      <InfoIcon class="dss__note-icon" />
      <Text type="body" color="muted" is="p">
        You can track this deposit in your transaction history.
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.dss__tick {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--p-success-50);
  border: 1.5px solid var(--p-success-200);
  flex-shrink: 0;
}

.dss__tick-icon {
  font-size: 22px;
  color: var(--p-success-700);
}

.dss__cards {
  width: 100%;
}

.dss__card {
  flex: 1;
  min-width: 0;
}

.dss__next {
  flex: 1;
  min-width: 0;
  align-self: stretch;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
  padding: 1.375rem 1.5rem;
  box-sizing: border-box;
}

.dss__steps {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

.dss__step-num {
  width: 22px;
  height: 22px;
  border-radius: 40px;
  background: var(--p-surface-100);
  flex-shrink: 0;
  margin-top: 1px;
}

.dss__step-text {
  min-width: 0;
}

.dss__note {
  background: var(--p-surface-100);
  border-radius: 12px;
  padding: 0.875rem 1.125rem;
}

.dss__note-icon {
  font-size: 18px;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
  margin-top: 2px;
}

@media (max-width: 992px) {
  .dss__cards {
    flex-direction: column;
  }
}
</style>
